import { capitalize, dropRight, each, filter, last, reduce } from "lodash";
import { createSelector } from "reselect";

import { DefaultFictionNarrativeLabelIds } from "../constants/defaultFictionNarrativeLabels";
import {
  ITEM_CARD_NARRATIVE_LIST_VIEW,
  ITEM_CARD_SEQUENCE_LIST_VIEW,
  MINDMAP_VIEW,
  NARRATIVE_VIEW,
  OUTLINE_VIEW,
  RELATIONSHIP_VIEW,
  SPREADSHEET_VIEW,
  SUBWAY_VIEW,
  TIMELINE_VIEW
} from "../constants/identifiers";
import interfaceIdentifiers from "../constants/interfaceIdentifiers";
import itemTypeIds from "../constants/itemTypeIds";
import userAgent from "../utils/userAgent";
import { ExternalLinkIdentifiers } from "./tutorialLinks";

export const getTipIdGenerator = groupId => tipId => `${groupId}/${tipId}`;

export const getViewNameLabel = (viewType, withFlash = false) => {
  let viewLabel;
  let interfaceId = null;
  switch (viewType) {
    case TIMELINE_VIEW:
      viewLabel = "Timeline";
      interfaceId = interfaceIdentifiers.TIMELINE;
      break;
    case SPREADSHEET_VIEW:
      viewLabel = "Spreadsheet";
      interfaceId = interfaceIdentifiers.SPREADSHEET;
      break;
    case RELATIONSHIP_VIEW:
      viewLabel = "Relationship";
      interfaceId = interfaceIdentifiers.RELATIONSHIP;
      break;
    case SUBWAY_VIEW:
      viewLabel = "Subway";
      interfaceId = interfaceIdentifiers.SUBWAY;
      break;
    case NARRATIVE_VIEW:
      viewLabel = "Narrative";
      interfaceId = interfaceIdentifiers.NARRATIVE;
      break;
    case OUTLINE_VIEW:
      viewLabel = "Outline";
      interfaceId = interfaceIdentifiers.OUTLINE;
      break;
    case MINDMAP_VIEW:
      viewLabel = "Mindmap";
      interfaceId = interfaceIdentifiers.MINDMAP;
      break;
    case ITEM_CARD_SEQUENCE_LIST_VIEW:
      viewLabel = "Chronological List";
      interfaceId = interfaceIdentifiers.MOBILE_CHRONOLOGICAL_LIST;
      break;
    case ITEM_CARD_NARRATIVE_LIST_VIEW:
      viewLabel = "Narrative Outline";
      interfaceId = interfaceIdentifiers.MOBILE_NARRATIVE_LIST;
      break;
  }
  return withFlash ? getFlashPlaceholder(interfaceId, viewLabel) : viewLabel;
};

export const getSwitchViewInstruction = viewType => {
  let result = null;
  //#!if mobile === true
// const needsPortrait = viewType === ITEM_CARD_SEQUENCE_LIST_VIEW || viewType === ITEM_CARD_NARRATIVE_LIST_VIEW;
// const orientation = needsPortrait ? "portrait" : "landscape";
// result = `Rotate your device to ${orientation} mode and open ${getViewNameLabel(viewType, false)}.`;
  //#!else
  result = `Open ${getViewNameLabel(viewType, true)} View. ${getLinkPlaceholder(
    ExternalLinkIdentifiers.HOW_TO_OPEN_VIEW,
    "Show me how"
  )}`;
  //#!endif
  return { label: result, isNumbered: true };
};

export const getSplitViewInstruction = (firstViewType, secondViewType) => {
  let result = null;
  result = `Open ${getViewNameLabel(firstViewType, true)} View in one split view, and ${getViewNameLabel(
    secondViewType,
    true
  )} View in another. ${getLinkPlaceholder(ExternalLinkIdentifiers.HOW_TO_OPEN_VIEW, "Show me how")}`;
  return { label: result, isNumbered: true };
};

export const getEnterEditModeInstruction = viewType => {
  const viewName = viewType ? `${getViewNameLabel(viewType)} View` : "the work area";
  return { label: `Long press on ${viewName} to enter Edit Mode`, isNumbered: true };
};

export const getRotateLandscape = () => {
  return { label: "Rotate your device into landscape mode", isNumbered: true };
};
export const getRotatePortrait = () => {
  return { label: "Rotate your device into portrait mode", isNumbered: true };
};

const isVowel = c => c === "a" || c === "e" || c === "i" || c === "o" || c === "u";

const withIndefiniteArticle = label => {
  return isVowel(label.charAt(0)) ? `an ${label}` : `a ${label}`;
};

const labelsToListSentence = (labels, operation = "and") => {
  const start = dropRight(labels, 1).join(", ");
  const lastItem = last(labels);
  return lastItem ? (start.length ? start + ` ${operation} ${lastItem}` : lastItem) : "";
};

export const Placeholders = {
  AN_EVENT_LABEL: `AN_EVENT_LABEL`,
  EVENT_LABEL: `EVENT_LABEL`,
  EVENT_CAPITAL_LABEL: `EVENT_CAPITAL_LABEL`,
  EVENT_PLURAL_LABEL: `EVENT_PLURAL_LABEL`,
  AN_ENTITY_LABEL: `AN_ENTITY_LABEL`,
  ENTITY_LABEL: `ENTITY_LABEL`,
  ENTITY_CAPITAL_LABEL: `ENTITY_CAPITAL_LABEL`,
  ENTITY_PLURAL_LABEL: `ENTITY_PLURAL_LABEL`,
  ENTITY_START_LABEL: `ENTITY_START_LABEL`,
  ENTITY_ICON: `ENTITY_ICON`,
  ENTITY_ICON_FLASH: `ENTITY_ICON_FLASH`,

  NARRATIVE_ITEM_LABEL: `NARRATIVE_ITEM_LABEL`,
  NARRATIVE_EXPOSITION_LABEL: `NARRATIVE_EXPOSITION_LABEL`,
  NARRATIVE_ITEM_CAPITAL_LABEL: `NARRATIVE_ITEM_CAPITAL_LABEL`,
  NARRATIVE_EXPOSITION_CAPITAL_LABEL: `NARRATIVE_EXPOSITION_CAPITAL_LABEL`,
  NARRATIVE_FOLDER_SECTION_TYPE_SCENE: `NARRATIVE_FOLDER_SECTION_TYPE_SCENE`,
  NARRATIVE_FOLDER_SECTION_TYPES: `NARRATIVE_FOLDER_SECTION_TYPES`,
  NARRATIVE_FOLDER_SECTION_TYPES_OR: `NARRATIVE_FOLDER_SECTION_TYPES_OR`,

  OTHER_ENTITY_TYPES: `OTHER_ENTITY_TYPES`,

  WRITING_APP: `WRITING_APP`,
  AVAILABLE_WRITING_APPS: `AVAILABLE_WRITING_APPS`
};

export const ICON_PREFIX = "ICON_";
export const LINK_PREFIX = "LINK_";
export const FLASH_PREFIX = "FLASH_";

const splitRegExStr = `(${ICON_PREFIX}[\\w\\|]+|${LINK_PREFIX}[\\w\\|]+|${FLASH_PREFIX}[\\w\\|\\:\\+\\-]+)`;
const splitIconRegEx = new RegExp(splitRegExStr, "g");
const iconComponentRegEx = new RegExp(`${ICON_PREFIX}(\\w+)\\_(\\w+)`);
const linkComponentRegEx = new RegExp(`${LINK_PREFIX}(\\w+)\\|(\\w+)`);
const flashComponentRegEx = new RegExp(`${FLASH_PREFIX}([\\w\\:\\-]+)\\|([\\w\\+]+)`);

export const splitTextAtIconsAndLinks = text => filter(text.split(splitIconRegEx), t => t);

export const getIconPlaceholder = (name, color = "iconMono") => `${ICON_PREFIX}${name}_${color}`;
export const getIconComponents = text => {
  const m = text.match(iconComponentRegEx);
  if (m) {
    return {
      icon: m[1],
      color: m[2]
    };
  }
  return null;
};

export const getLinkPlaceholder = (id, text) => `${LINK_PREFIX}${id}|${text.replace(/\s/g, "_")}`;
export const getLinkComponents = text => {
  const m = text.match(linkComponentRegEx);
  if (m) {
    return {
      id: m[1],
      text: m[2].replace(/_/g, " ")
    };
  }
  return null;
};

export const getFlashPlaceholder = (id, text) => `${FLASH_PREFIX}${id}|${text.replace(/\s/g, "_")}`;
export const getFlashComponents = text => {
  const m = text.match(flashComponentRegEx);
  if (m) {
    return {
      id: m[1],
      text: m[2].replace(/_/g, " ")
    };
  }
  return null;
};

const ReplacementRegExps = reduce(
  Placeholders,
  (result, value, key) => {
    result[key] = new RegExp(`\\b${value}\\b`, "g");
    return result;
  },
  {}
);

const getReplacementStrings = createSelector(
  // input
  (...params) => params[0],
  (...params) => params[1],
  (...params) => params[2],
  (...params) => params[3],
  (...params) => params[4],
  (...params) => params[5],
  // output
  (sidebarTypes, orderedNarrativeTypes, typesById, defaultSequenceType, narrativeSectionTypes, syncAppName) => {
    let labels = {};

    // Find our default event type
    const eventType = typesById.get(defaultSequenceType);
    const eventLabel = eventType.label.toLowerCase();

    // And populate its labels
    labels[Placeholders.EVENT_LABEL] = eventLabel;
    labels[Placeholders.EVENT_CAPITAL_LABEL] = capitalize(eventType.label);
    labels[Placeholders.AN_EVENT_LABEL] = withIndefiniteArticle(eventLabel);
    labels[Placeholders.EVENT_PLURAL_LABEL] = eventType.pluralLabel.toLowerCase();

    // Find our default entity type
    const entityType = sidebarTypes && sidebarTypes.size ? sidebarTypes.first() : null;

    if (entityType) {
      const entityLabel = entityType.label.toLowerCase();
      labels[Placeholders.ENTITY_LABEL] = entityLabel;
      labels[Placeholders.ENTITY_CAPITAL_LABEL] = capitalize(entityLabel);
      labels[Placeholders.AN_ENTITY_LABEL] = withIndefiniteArticle(entityLabel);
      labels[Placeholders.ENTITY_PLURAL_LABEL] = entityType.pluralLabel.toLowerCase();
      let startLabel = entityType.startDisplayLabel.toLowerCase();
      if (startLabel.indexOf("date") < 0) {
        startLabel = `${startLabel} date`;
      }
      labels[Placeholders.ENTITY_START_LABEL] = startLabel;
      labels[Placeholders.ENTITY_ICON] = getIconPlaceholder(entityType.icon, entityType.color);
      labels[Placeholders.ENTITY_ICON_FLASH] = getFlashPlaceholder(entityType.id, "icon");
    }

    // List other entity types
    const otherEntityTypes = sidebarTypes.filter(s => s !== entityType);
    if (otherEntityTypes) {
      labels[Placeholders.OTHER_ENTITY_TYPES] = labelsToListSentence(
        otherEntityTypes.map(t => t.pluralLabel).toArray()
      );
    }

    // Find our default narrative type which is allowed in both sequence and narrative
    let narrativeItemTypes = orderedNarrativeTypes
      ? orderedNarrativeTypes.filter(t => t.id !== itemTypeIds.NARRATIVE_FOLDER_TYPE_ID && t.showInSequence)
      : null;
    // If we didn't find any that can exist in narrative and timeline, then take anything
    if (!narrativeItemTypes || narrativeItemTypes.size === 0) {
      narrativeItemTypes = orderedNarrativeTypes
        ? orderedNarrativeTypes.filter(t => t.id !== itemTypeIds.NARRATIVE_FOLDER_TYPE_ID)
        : null;
    }
    const narrativeItemType = narrativeItemTypes && narrativeItemTypes.size ? narrativeItemTypes.first() : null;

    if (narrativeItemType) {
      labels[Placeholders.NARRATIVE_ITEM_LABEL] = narrativeItemType.label.toLowerCase();
      labels[Placeholders.NARRATIVE_ITEM_CAPITAL_LABEL] = capitalize(narrativeItemType.label.toLowerCase());
    }

    // Find our Narrative Exposition Type
    const narrativeExpositionTypes = orderedNarrativeTypes
      ? orderedNarrativeTypes.filter(t => t.id !== itemTypeIds.NARRATIVE_FOLDER_TYPE_ID && !t.showInSequence)
      : null;
    const narrativeExpositionType =
      narrativeExpositionTypes && narrativeExpositionTypes.size ? narrativeExpositionTypes.first() : null;

    if (narrativeExpositionType) {
      labels[Placeholders.NARRATIVE_EXPOSITION_LABEL] = narrativeExpositionType.label.toLowerCase();
      labels[Placeholders.NARRATIVE_EXPOSITION_CAPITAL_LABEL] = capitalize(narrativeExpositionType.label.toLowerCase());
    }

    // Find list of folder section types
    let sceneIndex = narrativeSectionTypes.findIndex(s => s.id === DefaultFictionNarrativeLabelIds.Scene);
    let sceneLabel = "Scene";
    if (sceneIndex >= 0) {
      sceneLabel = narrativeSectionTypes.get(sceneIndex).label;
    } else {
      // If there is no scene, then we will take the first 2 items
      sceneIndex = Math.min(2, narrativeSectionTypes.length - 1);
    }
    const folderSectionTypes = narrativeSectionTypes
      .slice(0, sceneIndex)
      .map(t => t.label)
      .toArray();

    labels[Placeholders.NARRATIVE_FOLDER_SECTION_TYPE_SCENE] = sceneLabel;
    labels[Placeholders.NARRATIVE_FOLDER_SECTION_TYPES] = labelsToListSentence(folderSectionTypes);
    labels[Placeholders.NARRATIVE_FOLDER_SECTION_TYPES_OR] = labelsToListSentence(folderSectionTypes, "or");
    labels[Placeholders.WRITING_APP] = syncAppName === "None" ? "Writing" : syncAppName;
    if (userAgent.isWindowsApp()) {
      labels[Placeholders.AVAILABLE_WRITING_APPS] = "Scrivener";
    } else {
      labels[Placeholders.AVAILABLE_WRITING_APPS] = "Scrivener or Ulysses";
    }

    return labels;
  }
);

export const replaceTutorialPlaceholders = (
  str,
  sidebarTypes,
  orderedNarrativeTypes,
  typesById,
  defaultSequenceType,
  narrativeSectionTypes,
  syncAppName
) => {
  if (!str) {
    return str;
  }
  const substitutions = getReplacementStrings(
    sidebarTypes,
    orderedNarrativeTypes,
    typesById,
    defaultSequenceType,
    narrativeSectionTypes,
    syncAppName
  );

  let newStr = str;

  each(substitutions, (value, key) => {
    const re = ReplacementRegExps[key];
    newStr = newStr.replace(re, value);
  });
  return newStr;
};
